"use client";
//css
import styles from "./index.module.scss";
import {
  queryFeedbackConfig,
  questionFeedback,
  FeedbackPropsType,
} from "@/app/api/robot/chatContent";
// view
import { Button, Form, Radio, Space, Spin } from "@douyinfe/semi-ui";
import { useState, useEffect, useMemo } from "react";
import {
  ReportBizTypeEnum,
  ReportEventEnum,
  ReportSceneTypeEnum,
  ReportParentContentEnum,
} from "@/app/types/typeConfig/report";
import {
  MessageType,
  MessageSender,
  HeaderType,
  RobotMessageType,
} from "@/app/store/types";
import {
  IMConfigTypeEnum,
  OriginType,
  FeedbackTypeEnum,
} from "@/app/types/typeConfig/chatQuestion";
// ts
import { useTranslations, useCurrentLocale } from "@/app/lib/LocaleProvider";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import { AIAnswerKnowledgeId } from "@/const/issueType";
import { isApp } from "@/app/bridge";
import { useCurrentUser } from "@/app/lib/AuthProvider";
import { createSessionId, createClientUUid } from "@/app/utils/string";
import { useMessageStore } from "@/app/store";

/** 点赞等场景：选中该 feedbackType 时，补充说明为必填 */
const FEEDBACK_TYPE_DETAIL_REQUIRED = 1000;

type FeedbackProps = {
  streamingId?: string;
  fromClassName?: string;
  templateProps?: {
    likeOrDislike: "like" | "dislike";
    requestId?: string;
  };
  parentContentInfo?: {
    parentContentId: string;
    parentContent: string;
    parentContentType: ReportParentContentEnum;
  };
  type?: "button" | "default";
  /** 提交反馈后获取回复模板的配置类型，默认为 techAiFAQ */
  configType?: IMConfigTypeEnum;
  onSubmit?: () => void;
  /** 仅展示已提交的点踩数据（与交互态同一套表单布局，不可编辑） */
  readOnly?: boolean;
  readonlyInitial?: {
    feedbackType: number;
    feedback: string;
    knowledgeId?: string;
  };
};

export default function Feedback({
  streamingId,
  fromClassName,
  templateProps,
  parentContentInfo,
  configType = IMConfigTypeEnum.techAiFAQ,
  type = "default",
  onSubmit,
  readOnly = false,
  readonlyInitial,
}: FeedbackProps) {
  const t = useTranslations();
  const _lng = useCurrentLocale();
  const user = useCurrentUser();
  const { setRobotMessage } = useMessageStore();
  const isLike = templateProps?.likeOrDislike === "like";
  const reply = t.aiFeedback.thanksAIFeedback;
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const reasonOptions = [
    { value: 2000, label: t.aiFeedback["2000"] },
    { value: 2001, label: t.aiFeedback["2001"] },
    { value: 2002, label: t.aiFeedback["2002"] },
    { value: 2003, label: t.aiFeedback["2003"] },
    { value: 2004, label: t.aiFeedback["2004"] },
    { value: 2005, label: t.aiFeedback["2005"] },
    { value: 2006, label: t.aiFeedback["2006"] },
  ];
  const [spinning, setSpinning] = useState(false);
  const [feedbackType, setFeedbackType] = useState<number | undefined>(() =>
    readOnly && readonlyInitial ? readonlyInitial.feedbackType : undefined,
  );
  const [submit, setSubmit] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState<string>("");
  const isDislike = templateProps?.likeOrDislike === "dislike";
  /** 只读回显：差评仅展示 feedback 文案，不含 knowledgeId */
  const readonlyFeedbackText = useMemo(() => {
    if (!readOnly || !readonlyInitial) return "";
    return readonlyInitial.feedback?.trim() ?? "";
  }, [readOnly, readonlyInitial?.feedback]);

  /** 点踩 / 点赞均展示补充说明；点踩不依赖「其他」类选项 */
  const showFeedbackTextArea = isLike || isDislike;
  /** 点踩未选原因时不可提交；点赞无 radio，始终可点提交 */
  const submitBlocked = isDislike && feedbackType === undefined;

  useEffect(() => {
    if (readOnly) return;
    const params = {
      languageCode: _lng,
      type: IMConfigTypeEnum.feedback,
    };
    queryFeedbackConfig(params)
      .then((res) => {
        setSpinning(false);
        const { content = "", content2 = "" } = res || {};
        const _content = isLike ? content : content2;
        setFeedbackContent(_content || reply);
      })
      .finally(() => setSpinning(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    if (readOnly && readonlyInitial) {
      setFeedbackType(readonlyInitial.feedbackType);
    }
  }, [readOnly, readonlyInitial?.feedbackType]);

  const handleSubmit = (values: Record<string, unknown>) => {
    if (readOnly || submit) return;
    const rawFeedback = (values["feedback"] as string) ?? "";
    /** 上报埋点：点踩非「其他」时 extendField2 仍用选项文案 */
    const feedbackForReport = isLike
      ? rawFeedback
      : feedbackType === FEEDBACK_TYPE_DETAIL_REQUIRED
        ? rawFeedback
        : "";
    setSubmit(true);
    const item = reasonOptions.find((item) => item.value === feedbackType);

    const content = item?.label || "";
    addToReportQueue({
      bizType: ReportBizTypeEnum.AI_CHAT,
      createTime: new Date().getTime(),
      scene: ReportSceneTypeEnum.AI_CONSULT,
      eventType: isLike
        ? ReportEventEnum.SUBMIT_LIKE_ADVICE
        : ReportEventEnum.SUBMIT_DISLIKE_ADVICE,
      extendField1: feedbackType,
      extendField2: isLike
        ? feedbackForReport
        : feedbackType === FEEDBACK_TYPE_DETAIL_REQUIRED
          ? feedbackForReport
          : content,
      parentContent: parentContentInfo?.parentContent,
      parentContentId: parentContentInfo?.parentContentId,
      parentContentType: parentContentInfo?.parentContentType,
      extendField4: streamingId || "",
    });
    const sessionId = createSessionId();
    const params: FeedbackPropsType = {
      userId: user?.cusId || sessionId,
      feedbackClass:
        templateProps?.likeOrDislike === "like"
          ? FeedbackTypeEnum.like
          : FeedbackTypeEnum.dislike,
      feedbackType,
      /** /robot/front/feedback 的 feedback：与表单 TextArea 提交内容一致 */
      feedback: rawFeedback,
      knowledgeId: AIAnswerKnowledgeId,
      sessionId,
      origin: isApp() ? OriginType.app : OriginType.web,
      requestId: templateProps?.requestId || "",
      extendField4: streamingId || "",
    };
    questionFeedback(params).then(() => {
      // 提交之后，去拿回复模板
      queryFeedbackConfig({
        languageCode: _lng,
        type: configType,
      }).then((res) => {
        setSpinning(false);
        const { content = "", content2 = "" } = res || {};
        // 如果接口返回为空，使用默认的 thanksAIFeedback 话语
        const _content = content || content2 || reply;
        // setRobotMessage({
        //   clientId: createClientUUid(MessageType.Text),
        //   content: _content,
        //   timestamp: Date.now(),
        //   streaming: true,
        //   type: MessageType.Text,
        //   headerType: HeaderType.MESSAGE,
        //   senderType: MessageSender.ROBOT,
        //   senderName: "智能助手",
        //   messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
        //   robotData: {
        //     parentContentInfo,
        //   },
        // });
        onSubmit?.();
      });
    });
  };

  const fieldDisabled = readOnly || submit;
  const formInitValues =
    readOnly && readonlyInitial
      ? {
          feedbackType: readonlyInitial.feedbackType,
          feedback: readonlyFeedbackText,
        }
      : undefined;

  return spinning ? (
    <Spin />
  ) : (
    <div
      className={`${styles.collectOrderInfo} ${fromClassName ? styles[fromClassName] : ""} ${readOnly ? styles.collectOrderInfoReadOnly : ""}`}
    >
      <Form
        key={
          readOnly && readonlyInitial
            ? `ro-${readonlyInitial.feedbackType}-${readonlyFeedbackText}`
            : "fb-form"
        }
        className={styles.form}
        layout="vertical"
        initValues={formInitValues}
        onSubmit={handleSubmit}
      >
        {isDislike && (
          <Form.RadioGroup
            field="feedbackType"
            label={
              readOnly
                ? t.aiFeedback.feedbackTypeLabelReadOnly
                : t.aiFeedback.feedbackTypeLabel
            }
            rules={
              readOnly
                ? []
                : [{ required: true, message: t.aiFeedback.feedbackTypeLabel }]
            }
            disabled={fieldDisabled}
            onChange={(e) => setFeedbackType(e.target.value)}
            type={type}
            className={`${styles.radioGroup} ${type === "button" ? styles.buttonStyle : ""}`}
          >
            <Space vertical align="start" className={styles.radioGroupSpace}>
              {reasonOptions.map((item, index) => (
                <Radio
                  key={index}
                  value={item.value}
                  className={`${styles.radio} ${`${item.value}` === `${feedbackType}` ? styles.active : ""}`}
                >
                  {item.label}
                </Radio>
              ))}
            </Space>
          </Form.RadioGroup>
        )}
        {showFeedbackTextArea && (
          <Form.TextArea
            key={isDislike ? "fb-dislike" : "fb-like"}
            field="feedback"
            noLabel={true}
            rules={
              readOnly
                ? []
                : isDislike
                  ? []
                  : [
                      {
                        required:
                          feedbackType === FEEDBACK_TYPE_DETAIL_REQUIRED,
                        message: t.aiFeedback.feedbackPlaceholder,
                      },
                    ]
            }
            className={styles.feedbackInput}
            rows={4}
            placeholder={t.aiFeedback.feedbackPlaceholder}
            maxLength={500}
            disabled={fieldDisabled}
          />
        )}
        {!readOnly && (
          <div className={styles.btnWrapper}>
            <Button
              className={`${styles.submitButton} ${submitBlocked ? styles.submitBlocked : ""}`}
              theme="solid"
              type="primary"
              htmlType="submit"
              disabled={submit || submitBlocked}
            >
              {t.aiFeedback.submit}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
}
